/**
 * templateLinks.js — pure helpers over the `TemplateLinkView[]` that Sun's E1 returns (story 224, `contrato-sun.md` §2).
 * The contract `null`s are read here, at the boundary, so the components work with `{}`, `[]` and `''`.
 */

/** The link as the editor uses it when there is none. */
export const NO_TEMPLATE_LINKS = { latestVersion: false, links: [] }

/**
 * Normalises an E1 response. Anything that is not a well-formed answer (a failure, a missing body) means "no links":
 * the editor then paints nothing of the template and edits and saves as before (RN-G2).
 * Each link's `parentUnitNodeId` (the `subworkflowNodeId` of the nearest enclosing linked SUBWORKFLOW unit, `null` for ROOT and
 * for a unit with none above it — adjustment 2026-10-29) is read here, at the boundary: `null` or absent becomes `''`.
 * @param {object} response `WorkflowTemplateLinks`
 * @returns {{latestVersion: boolean, links: object[]}}
 */
export const readTemplateLinks = (response) => {
  const links = Array.isArray(response?.links)
    ? response.links.map((link) => ({ ...link, parentUnitNodeId: typeof link?.parentUnitNodeId === 'string' ? link.parentUnitNodeId : '' }))
    : []
  return { latestVersion: response?.latestVersion === true, links }
}

/** @returns {object} the ROOT link, or `{}` when the workflow has none */
export const rootLinkOf = (links = []) => links.find((link) => link.scope === 'ROOT') || {}

/** H9: versions travel as floats (`1.0`); an integer one is painted without decimals. JSON numbers already drop `.0`. */
export const formatTemplateVersion = (version) => String(Number(version))

/** @returns {object} the SUBWORKFLOW link whose unit node is `nodeId`, or `{}` */
export const linkOfNode = (links = [], nodeId = '') =>
  links.find((link) => link.scope === 'SUBWORKFLOW' && link.subworkflowNodeId === nodeId) || {}

/** @returns {object[]} the links that carry a newer-template notice, in the order Sun sent them (ROOT first) */
export const linksWithUpdate = (links = []) => links.filter((link) => link.updateAvailable === true)

/**
 * The links the banner above the canvas speaks for: only the workflow being viewed, never its children (their notice lives in
 * the General Properties of their node). In the editor of a piece workflow that is its ROOT link; inside a `SubworkflowDialog`
 * (`unitNodeId` = the node of the subworkflow being viewed) it is the link of that subworkflow.
 * @returns {object[]}
 */
export const viewedUnitLinks = (links = [], unitNodeId = '') => unitNodeId
  ? links.filter((link) => link.scope === 'SUBWORKFLOW' && link.subworkflowNodeId === unitNodeId)
  : links.filter((link) => link.scope === 'ROOT')

/**
 * S111 (RN-02.5): the canvas nodes that carry the notice mark — the top-level WORKFLOW node that contains each unit with a
 * notice and the unit's own node (that is the one seen inside a `SubworkflowDialog`). A graph only holds the ones it draws.
 * @returns {string[]}
 */
export const templateUpdateNodeIds = (links = []) => {
  const ids = new Set()
  linksWithUpdate(links).forEach((link) => {
    if (link.topLevelNodeId) ids.add(link.topLevelNodeId)
    if (link.subworkflowNodeId) ids.add(link.subworkflowNodeId)
  })
  return Array.from(ids)
}

/**
 * S111: patches the already rendered canvas nodes in place (never a rebuild: that would trigger `fitView`) — same technique as
 * `applyProcessOriginHighlight`. Returns the SAME array when nothing changes, so the caller does not repaint for nothing.
 */
export const applyTemplateUpdateMark = (nodes = [], nodeIds = []) => {
  let changed = false
  const painted = nodes.map((node) => {
    const marked = nodeIds.includes(node?.id)
    if ((node?.data?.templateUpdateAvailable === true) === marked) {
      return node
    }
    changed = true
    return { ...node, data: { ...node.data, templateUpdateAvailable: marked } }
  })
  return changed ? painted : nodes
}

// ---- Pending template updates (adjustment 2026-10-29) ---------------------------------------------------------------------------
// Confirming an update in the impact dialog no longer saves anything: it leaves an INTENT in the editor, and the window's Save
// sends it (`templateUpdates` of the save body) — like an accepted operation-library update. Revert discards it with the rest.

/** What a notice row knows about its unit when nothing is pending. */
export const NO_UNIT_STATE = { pending: {}, decision: {} }

/** @returns {string} the unit a link, an impact or an intent speaks for: `''` is the ROOT, otherwise its subworkflow node id */
export const unitIdOf = (unit = {}) => unit.scope === 'ROOT' ? '' : (unit.subworkflowNodeId || '')

/**
 * Whether unit `outer` strictly contains unit `inner` (adjustment 2026-10-29). The ROOT contains every subworkflow unit; a
 * subworkflow unit contains the units whose chain of `parentUnitNodeId` (E1: the nearest enclosing linked SUBWORKFLOW unit, `''`
 * at the top) reaches it. The chain is walked by node id over the links of E1, so two units with the same code are never confused.
 * @param {object} outer a link or an intent
 * @param {object} inner a link or an intent
 * @param {object[]} links the `TemplateLinkView[]` of E1 (`readTemplateLinks`), which locate each parent
 * @returns {boolean}
 */
export const containsUnit = (outer = {}, inner = {}, links = []) => {
  if (inner.scope !== 'SUBWORKFLOW' || unitIdOf(inner) === unitIdOf(outer)) {
    return false
  }
  if (outer.scope === 'ROOT') {
    return true
  }
  const visited = new Set()
  let parentId = inner.parentUnitNodeId || ''
  while (parentId && !visited.has(parentId)) {
    if (parentId === outer.subworkflowNodeId) {
      return true
    }
    visited.add(parentId)
    parentId = linkOfNode(links, parentId).parentUnitNodeId || ''
  }
  return false
}

/**
 * The intent the editor keeps when the user confirms the update of a unit: the unit (as E1 locates it), the target template
 * version and the impact (E3) the user accepted, which is what "View changes" shows again.
 * @param {object} link the `TemplateLinkView` of the unit
 * @param {object} impact the `TemplateUpdateImpact` confirmed
 */
export const templateUpdateIntent = (link = {}, impact = {}) => ({
  scope: link.scope === 'ROOT' ? 'ROOT' : 'SUBWORKFLOW',
  subworkflowNodeId: unitIdOf(link),
  parentUnitNodeId: link.parentUnitNodeId || '',
  // The top-level node that holds the unit (E1): deleting it retires the unit's update even when no link of a container says so (G4).
  topLevelNodeId: link.topLevelNodeId || '',
  targetTemplateWorkflowId: impact.toTemplateWorkflowId,
  targetTemplateVersion: impact.toTemplateVersion,
  impact
})

/**
 * Adds (or replaces) the intent of a unit. A ROOT update replaces the whole process, so it goes alone (Sun rejects ROOT together
 * with others, `TEMPLATE_UPDATE_ROOT_NOT_ALONE`); a subworkflow's drops the intents of the units it contains, which it replaces.
 * @param {object[]} links the links of E1, to know which units an intent contains
 * @returns {object[]}
 */
export const withTemplateUpdate = (pending = [], intent = {}, links = []) => intent.scope === 'ROOT'
  ? [intent]
  : [...pending.filter((other) => unitIdOf(other) !== unitIdOf(intent) && !containsUnit(intent, other, links)), intent]

/** @returns {object} the pending intent of the unit of `link`, or `{}` */
export const pendingUpdateOf = (pending = [], link = {}) =>
  pending.find((intent) => intent.scope === (link.scope === 'ROOT' ? 'ROOT' : 'SUBWORKFLOW') && unitIdOf(intent) === unitIdOf(link)) || {}

// ---- Pending decisions (HU-05, RN-07.5) -------------------------------------------------------------------------------------------
// Editing a linked unit asks "publish or unlink"; choosing writes nothing: it leaves a DECISION in the editor and the window's Save
// sends it (`templateLinkChanges` of the save body). A unit has at most one update and one decision (R8), in that order.

export const TEMPLATE_LINK_ACTION = { PUBLISH: 'PUBLISH', UNLINK: 'UNLINK' }

/**
 * The decision the editor keeps when the user answers the question of a unit.
 * @param {object} link the `TemplateLinkView` of the unit (or the one made for a template just inserted, RN-07.19)
 * @param {string} action a `TEMPLATE_LINK_ACTION`
 * @param {string} indexValueName the index chosen for the new template version ('' for Unlink or with the index setting off)
 */
export const templateDecision = (link = {}, action = '', indexValueName = '') => ({
  scope: link.scope === 'ROOT' ? 'ROOT' : 'SUBWORKFLOW',
  subworkflowNodeId: unitIdOf(link),
  parentUnitNodeId: link.parentUnitNodeId || '',
  topLevelNodeId: link.topLevelNodeId || '',
  action,
  indexValueName: action === TEMPLATE_LINK_ACTION.PUBLISH ? indexValueName : '',
  templateCode: link.templateCode || '',
  templateVersion: link.templateVersion
})

/** Adds (or replaces) the decision of a unit. */
export const withTemplateDecision = (pending = [], decision = {}) => [...pending.filter((other) => unitIdOf(other) !== unitIdOf(decision)), decision]

/**
 * The version a publication starts from (RN-07.7): the one the unit is linked to, or the target of its pending update.
 * @returns {number}
 */
export const publishBaseVersionOf = (link = {}, pendingUpdate = {}) =>
  pendingUpdate.scope ? Number(pendingUpdate.targetTemplateVersion) : Number(link.templateVersion)

/**
 * G7 (RN-07.24): the number of the newest version of the series when it is newer than the base of the publication; `0` when there
 * is none (no field, `null`, or not newer: an equal number is not newer).
 * @returns {number}
 */
export const newerTemplateVersionOf = (link = {}, baseVersion = 0) => {
  const latest = Number(link.latestTemplateVersion)
  return Number.isFinite(latest) && link.latestTemplateVersion !== null && latest > baseVersion ? latest : 0
}
